import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { auth } from '../../firebase';
import NewNotificationToast from '../Notifications/NewNotificationToast';
import { updateNotification } from '../../redux/ducks/notifications';
import Notification from '../../models/Notification';
import { isObjectNotEmpty, isStringNotEmpty } from '../../utils/objectUtils';
import { normalizeFunnelRoute } from '../../utils/funnelRouteUtils';

// deferred FCM/notification setup. Lives inside the Router so it can re-evaluate on SPA route
// changes (the funnel never shows the permission prompt; the next off-funnel navigation
// re-attempts), and defers 2s past sign-in/navigation so FCM work never competes with paint.
const NotificationSetup = () => {
	//#region useDispatch

	const dispatch = useDispatch();

	//#endregion
	//#region useLocation

	const location = useLocation();

	//#endregion
	//#region useStates

	const [currentUser, setCurrentUser] = useState(null);

	//#endregion
	//#region useRefs

	// keyed by uid so a second user signing in on the same tab gets their own token; only the
	// granted-but-token-failed state stays unlatched so it retries on the next navigation
	const setupDoneForUidRef = useRef(null);
	// the foreground-message listener attaches at most once per page load
	const listenerAttachedRef = useRef(false);

	//#endregion
	//#region useEffects

	useEffect(() => {
		const unsubscribe = auth.onAuthStateChanged((user) => {
			setCurrentUser(user ?? null);
		});

		return () => unsubscribe();
	}, []);

	useEffect(() => {
		if (!currentUser || setupDoneForUidRef.current === currentUser.uid) { return undefined; }

		let cancelled = false;

		const timer = setTimeout(async () => {
			if (cancelled || setupDoneForUidRef.current === currentUser.uid) { return; }

			// never surface the browser permission prompt mid-funnel; already-granted browsers
			// still get their token wired up silently
			const currentRoute = normalizeFunnelRoute(window.location.pathname, window.location.search);
			if (currentRoute.isFunnelRoute && (typeof window.Notification === 'undefined' || window.Notification.permission !== 'granted')) {
				return;
			}

			try {
				const { default: NotificationService } = await import('../../services/NotificationService');

				// Initialize FCM only if not in an in-app browser
				const fcmInitialized = await NotificationService.initialize();
				console.log('fcmInitialized', fcmInitialized);
				if (fcmInitialized && !cancelled) {
					// Request notification permission and save token
					const tokenRegistered = await NotificationService.requestPermission(currentUser.uid);
					const permissionGranted = typeof window.Notification !== 'undefined' && window.Notification.permission === 'granted';
					if (tokenRegistered === true || !permissionGranted) {
						// a dismissed prompt (permission still 'default') must latch too, or every
						// route change would re-surface the browser prompt
						setupDoneForUidRef.current = currentUser.uid;
					}

					if (!permissionGranted) {
						return;
					}

					if (listenerAttachedRef.current) {
						return;
					}
					listenerAttachedRef.current = true;

					// Set up message listener once permission is granted, independent of this
					// run's token fetch - previously-registered browsers still receive messages
					NotificationService.onMessageListener()
						.then((payload) => {
							// Handle foreground messages here
							console.log('Received foreground message:', payload);
							if (isObjectNotEmpty(payload?.data) && isStringNotEmpty(payload?.data?.notificationId)) {
								let newNotification = new Notification({ ...(payload?.data ?? {}), cta: { url: payload?.data?.url ?? null, label: payload?.data?.label ?? null } }).toDto();
								newNotification.id = payload?.data?.notificationId;
								toast(<NewNotificationToast notification={newNotification} />, { toastId: payload?.data?.notificationId });
								dispatch(updateNotification(newNotification));
								// You can show a toast notification or handle it however you want
							}
						})
						.catch((err) => console.error('Error receiving message:', err));
				}
			} catch (err) {
				console.error('Error initializing notifications:', err);
			}
		}, 2000);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [currentUser, location.pathname]);

	//#endregion

	return null;
};

export default NotificationSetup;
